import { Head } from 'vite-react-ssg';
import { Code, Terminal, Zap, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const DeepseekCoder = () => {
  return (
    <>
      <Head>
        <title>DeepSeek Coder 2026 — Free AI Code Generation &amp; Review</title>
        <meta 
          name="description" 
          content="DeepSeek Coder guide: free open-source AI for code generation, refactoring and review. Benchmarks vs GPT-5 and Claude, setup and API examples for 2026." 
        />
        <link rel="canonical" href="https://deepseek.ai/deepseek-coder" />
      </Head>

      <main className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="space-y-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold tracking-tight mb-4">DeepSeek Coder</h1>
            <p className="text-xl text-gray-600">
              Advanced AI-powered code generation and analysis
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button size="lg" asChild>
                <a href="https://platform.deepseek.com" target="_blank" rel="noopener noreferrer">
                  Try DeepSeek Coder
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="https://github.com/deepseek-ai/DeepSeek-Coder" target="_blank" rel="noopener noreferrer">
                  <Code className="mr-2 h-4 w-4" />
                  View on GitHub
                </a>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Terminal className="h-5 w-5 text-green-500" />
                  Code Generation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Generate high-quality code across multiple programming languages with context-aware suggestions and completions.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  Code Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Identify bugs, security vulnerabilities, and performance issues with advanced static analysis.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-blue-500" />
                  Documentation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Automatically generate comprehensive documentation for your code and APIs.
                </p>
              </CardContent>
            </Card>
          </div>

          <section className="mt-16">
            <h2 className="text-3xl font-bold mb-6">Code Examples</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Python Example</h3>
                <pre className="overflow-x-auto">
                  <code className="text-sm">
{`from deepseek_coder import CodeGenerator

generator = CodeGenerator()
code = generator.generate(
    prompt="Create a function to sort a list",
    language="python"
)`}
                  </code>
                </pre>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">JavaScript Example</h3>
                <pre className="overflow-x-auto">
                  <code className="text-sm">
{`import { DeepSeekCoder } from '@deepseek/coder';

const coder = new DeepSeekCoder();
const result = await coder.analyze({
  code: sourceCode,
  language: 'javascript'
});`}
                  </code>
                </pre>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default DeepseekCoder;